import React, { useState, useEffect, useMemo } from "react";
import { useLocation } from "react-router-dom";

// ** Icons
import ArrowRight from "mdi-material-ui/ArrowRight";
import BookOpenPageVariant from "mdi-material-ui/BookOpenPageVariant";
import AlertCircleOutline from "mdi-material-ui/AlertCircleOutline";

// ** Layout & data
import UserLayout from "src/layouts/UserLayout";
import CourseCardV3 from "src/layouts/components/CourseCardV3";
import { courses_list } from "src/api/courses";
import { courseProgress } from "src/@core/utils/course-progress";
import { pickContinueCourse } from "src/@core/utils/course-continue";
import { pathWithPreservedSearch } from "src/@core/utils/urlQueryPreservation";

// ** Hooks & styles
import { useAuth } from "src/hooks/useAuth";
import "src/styles/style-v3.css";

const Home = () => {
  // ** Hooks
  const auth = useAuth();
  const location = useLocation();
  const [isLoading, setIsLoading] = useState(true);
  const [courses, setCourses] = useState([]);
  const [errorLoadingData, setErrorLoadingData] = useState(false);

  useEffect(() => {
    if (auth?.user) loadCourses();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadCourses = async () => {
    const response = await dbGetCoursesList(auth.user);

    if (!response.status) {
      setErrorLoadingData(true);
    } else {
      setCourses(response.data);
    }
    setIsLoading(false);
  };

  const dbGetCoursesList = (auth_data) => {
    return new Promise((resolve) => {
      try {
        courses_list(auth_data)
          .then((res) => resolve({ status: true, data: res.data }))
          .catch((err) => resolve({ status: false, data: err }));
      } catch (error) {
        resolve({ status: false, data: error });
      }
    });
  };

  // Learner momentum, derived from the real course list (no fabricated stats)
  const summary = useMemo(() => buildSummary(courses, location.search), [courses, location.search]);
  const firstName = getFirstName(auth?.user?.name);

  return (
    <UserLayout>
      <div className="spv3">
        <HeroBand loading={isLoading} firstName={firstName} summary={summary} />

        <section className="spv3-section">
          <div className="spv3-section__head">
            <h2 className="spv3-section__title">Os meus cursos</h2>
            {!isLoading && !errorLoadingData && courses.length > 0 && (
              <span className="spv3-count">{courses.length}</span>
            )}
          </div>

          {errorLoadingData ? (
            <ErrorState />
          ) : isLoading ? (
            <div className="spv3-grid">
              {Array.apply(null, { length: 4 }).map((e, i) => (
                <CourseCardV3 key={i} loading />
              ))}
            </div>
          ) : courses.length === 0 ? (
            <EmptyState />
          ) : (
            <div className="spv3-grid">
              {courses.map((item, i) => (
                <CourseCardV3 key={i} data={item} />
              ))}
            </div>
          )}
        </section>
      </div>
    </UserLayout>
  );
};

// HELPERS

/******************************************************************
 * ##: HeroBand
 * Learner momentum band: greeting, supporting stats and a one-click
 * "continue learning" action, with a circular ring showing the
 * average completion across all courses (the page signature).
 * @param {Object} props - loading / firstName / summary
 * @returns {JSX.Element} - the `.spv3-hero` band
 * History:
 * 05-07-2026: Created
 * 26-07-2026: Bug fix - stats/ring skeleton looped forever with zero courses; skeleton now shows only while loading, real values (0 included) once loaded
 ******************************************************************/
const HeroBand = ({ loading, firstName, summary }) => {
  const title = firstName ? `Olá, ${firstName} 👋` : "Bem-vindo de volta 👋";
  const ringValue = loading ? 0 : summary.averageProgress;
  const complete = !loading && ringValue >= 100;

  return (
    <div className="spv3-hero">
      <span className="spv3-hero__glow" />

      <div className="spv3-hero__main">
        <span className="spv3-hero__eyebrow">A minha aprendizagem</span>
        <h1 className="spv3-hero__title">{title}</h1>
        <p className="spv3-hero__subtitle">Retoma os teus cursos e continua exatamente de onde paraste.</p>

        {loading ? (
          <div className="spv3-hero__metrics">
            {Array.apply(null, { length: 3 }).map((e, i) => (
              <div className="spv3-metric" key={i}>
                <span className="spv3-metric__value">
                  <span className="spv3-skeleton" style={{ display: "inline-block", width: 40, height: 22 }} />
                </span>
                <span className="spv3-metric__label">
                  <span className="spv3-skeleton" style={{ display: "inline-block", width: 60, height: 11 }} />
                </span>
              </div>
            ))}
          </div>
        ) : (
          <>
            <div className="spv3-hero__metrics">
              <div className="spv3-metric">
                <span className="spv3-metric__value">{summary.total}</span>
                <span className="spv3-metric__label">{summary.total === 1 ? "Curso" : "Cursos"}</span>
              </div>
              <div className="spv3-metric">
                <span className="spv3-metric__value">{summary.completed}</span>
                <span className="spv3-metric__label">Concluídos</span>
              </div>
              <div className="spv3-metric">
                <span className="spv3-metric__value">{summary.lessonsDone}</span>
                <span className="spv3-metric__label">Aulas vistas</span>
              </div>
            </div>

            {summary.continueHref && (
              <div className="spv3-hero__actions">
                <a className="spv3-btn spv3-btn--primary" href={summary.continueHref}>
                  {summary.continueLabel}
                  <ArrowRight className="spv3-btn__arrow" />
                </a>
              </div>
            )}
          </>
        )}
      </div>

      <div className="spv3-hero__aside">
        <div
          className={`spv3-ring${complete ? " spv3-ring--complete" : ""}`}
          style={{ "--val": ringValue }}
          role="img"
          aria-label={`Progresso médio: ${ringValue}%`}
        >
          <span className="spv3-ring__value">
            {ringValue}
            <span>%</span>
          </span>
        </div>
        <span className="spv3-ring__label">Progresso médio</span>
      </div>
    </div>
  );
};

/******************************************************************
 * ##: EmptyState
 * Shown when the learner has no courses on their account.
 * @returns {JSX.Element} - the `.spv3-empty` panel
 * History:
 * 05-07-2026: Created
 ******************************************************************/
const EmptyState = () => (
  <div className="spv3-empty">
    <span className="spv3-empty__icon">
      <BookOpenPageVariant />
    </span>
    <h3 className="spv3-empty__title">Ainda não tens cursos</h3>
    <p className="spv3-empty__text">
      Assim que um curso for adicionado à tua conta, aparece aqui pronto para começares a aprender.
    </p>
  </div>
);

/******************************************************************
 * ##: ErrorState
 * Shown when the course list fails to load.
 * @returns {JSX.Element} - the `.spv3-error` panel
 * History:
 * 05-07-2026: Created
 ******************************************************************/
const ErrorState = () => (
  <div className="spv3-error">
    <span className="spv3-error__icon">
      <AlertCircleOutline />
    </span>
    <h3 className="spv3-error__title">Não foi possível carregar os cursos</h3>
    <p className="spv3-error__text">
      Tenta novamente mais tarde. Se o erro continuar, contacta o suporte através de <strong>suporte@salespark.io</strong>.
    </p>
  </div>
);

/******************************************************************
 * ##: buildSummary
 * Aggregates the learner momentum from the course list: totals,
 * completed count, lessons watched, average progress and the best
 * "continue" target. The target is delegated to pickContinueCourse,
 * which only ever points to a course the student can still open
 * (never an ended-access course) and prefers the most recent
 * activity; continueHref is null when there is nothing to open, so
 * the hero action stays hidden.
 * @param {Array} courses - course records from /users/courses
 * @param {String} search - current location.search (preserved on links)
 * @returns {Object} - summary used by the hero band
 * History:
 * 05-07-2026: Created
 * 05-07-2026: Target via pickContinueCourse (access-aware, most recent activity); expose continueLabel
 ******************************************************************/
const buildSummary = (courses, search) => {
  const total = courses?.length || 0;
  if (total === 0) {
    return { total: 0, completed: 0, lessonsDone: 0, averageProgress: 0, continueHref: null, continueLabel: null };
  }

  let progressSum = 0;
  let completed = 0;
  let lessonsDone = 0;

  courses.forEach((course) => {
    const progress = courseProgress(course);
    progressSum += progress;
    if (progress >= 100) completed += 1;
    lessonsDone += (course?.courses_students?.lessonStatus || []).filter((lesson) => lesson.completed === true).length;
  });

  const averageProgress = Math.round(progressSum / total);

  // Only ever resume a course with active access; hide the action otherwise
  const { target, isReview } = pickContinueCourse(courses);
  const continueHref = target?.course_id ? pathWithPreservedSearch(`/courses/${target.course_id}`, search) : null;
  const continueLabel = isReview ? "Rever os cursos" : "Continuar a aprender";

  return { total, completed, lessonsDone, averageProgress, continueHref, continueLabel };
};

/******************************************************************
 * ##: getFirstName
 * Extracts the first name from the user's full name for the greeting.
 * @param {String} name - user full name
 * @returns {String} - first name, or empty string when unavailable
 * History:
 * 05-07-2026: Created
 ******************************************************************/
const getFirstName = (name) => {
  if (!name || typeof name !== "string") return "";
  return name.trim().split(/\s+/)[0] || "";
};

export default Home;
